import {cloneDeep} from 'lodash-es';
import {FuseNavigationItem} from '@fuse/components/navigation';
import {Navigation} from 'app/core/navigation/navigation.types';

/** Minimal asset shape needed to build Analytics aside items (matches LuzmoAsset). */
export interface LuzmoNavAsset {
    assetId: string;
    assetType: string;
    displayName: string;
    displayOrder: number;
}

/** Collapsable "BI Dashboard" section in the Analytics aside. */
export const LUZMO_NAV_PLACEHOLDER_ID = 'luzmo-reports';

/** Compact child under BI Dashboard — title only; bullet/indent via luzmo-bi-nav.scss. */
export function luzmoAssetToNavItem(asset: LuzmoNavAsset): FuseNavigationItem {
    const name = (asset.displayName || asset.assetId || '').trim();
    return {
        id: `luzmo-asset-${asset.assetId}`,
        title: name,
        type: 'basic',
        link: `/analytics/luzmo/${asset.assetId}`,
        classes: {
            wrapper: 'luzmo-bi-child'
        },
        meta: {
            assetId: asset.assetId,
            assetType: asset.assetType
        }
    };
}

/**
 * Fills the Analytics aside "BI Dashboard" collapsable with one child per Luzmo asset
 * (sorted by displayOrder). When there are no assets, the section is removed.
 */
export function injectLuzmoAssetsIntoNavigation(navigation: Navigation, assets: LuzmoNavAsset[]): Navigation {
    const next = cloneDeep(navigation);
    const sorted = (assets ?? []).slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    const luzmoItems = sorted.map(luzmoAssetToNavItem);

    next.compact = (next.compact ?? []).map(item => {
        if (item.id !== 'analytics' || !item.children) {
            return item;
        }

        const children = item.children
            .filter(child => !child.id?.startsWith('luzmo-asset-'))
            .map(child => {
                if (child.id !== LUZMO_NAV_PLACEHOLDER_ID) {
                    return child;
                }
                if (!luzmoItems.length) {
                    return null;
                }
                return {
                    ...child,
                    title: 'BI Dashboard',
                    type: 'collapsable' as const,
                    classes: {
                        ...(child.classes || {}),
                        wrapper: 'luzmo-bi-section'
                    },
                    children: luzmoItems
                };
            })
            .filter((child): child is FuseNavigationItem => !!child);

        return {...item, children};
    });

    return next;
}
